import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import Group from 'components/utils/Group'
import WalledContent from 'components/Common/WalledContent'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import BedbankPackageTileCheapestPricing from './BedbankPackageTileCheapestPricing'
import BedbankCancellationPolicy from '../BedbankCancellationPolicy'
import BedbankPromotionDetails from '../BedbankPromotionDetails'
import BodyText from 'components/Luxkit/Typography/BodyText'

const LabelWrapper = styled.div`
  margin-top: ${rem(4)};
`

const defaultRooms = [generateRoomOccupants()]

interface Props {
  rate: App.BedbankRate;
  offer: App.BedbankOffer;
  rooms?: Array<App.Occupants>;
  checkInDate?: string;
  checkOutDate?: string;
  luxPlusLPPEnabled?: boolean;
  packageView:App.BedbankOfferPackageView;
  detailsModalMode: 'room-and-package' | 'room-only';
  hasMultipleCancellationPolicies: boolean;
  showPartialRefundPolicyLabel?: boolean;
}

function BedbankRoomCheapestOptionRateMobile(props: Props) {
  const {
    rate,
    offer,
    rooms = defaultRooms,
    checkInDate,
    checkOutDate,
    luxPlusLPPEnabled,
    packageView,
    detailsModalMode,
    hasMultipleCancellationPolicies,
    showPartialRefundPolicyLabel,
  } = props

  const { bundleRates, promotions } = packageView
  const bundleRate = bundleRates.find((bundleRate) => bundleRate.id === rate.id) ?? rate
  const hotelPrice = rate.totals.inclusive + (rate.totals.propertyFees ?? 0)
  const propertyFees = rate.totals.propertyFees ?? 0

  const totalPrices = getDisplayTotalPrice({
    hotelPrice,
    hotelMemberPrice: ((rate.totals.memberInclusive ?? 0) > 0 ? (rate.totals.memberInclusive ?? 0) + propertyFees : 0),
    hotelValue: rate.value,
    bundlePrice: (bundleRate?.totals.inclusive || hotelPrice) + (bundleRate?.totals.propertyFees || propertyFees),
    bundleValue: bundleRate?.value,
    checkIn: checkInDate,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })

  const promotion = promotions[rate.id]

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorBedbankOptions(rate, {
      price: totalPrices.totals.price,
      memberPrice: totalPrices.totals.memberPrice,
      propertyFees,
    }),
  ], [totalPrices.totals.memberPrice, totalPrices.totals.price, rate, propertyFees])

  return (
    <Group direction="vertical" gap={16}>
      {promotion && <BedbankPromotionDetails
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        offer={offer}
        promotion={promotion}
        rate={rate}
      />}

      <Group direction="vertical" gap={8} noDisplayWhenEmpty>
        {hasMultipleCancellationPolicies && (
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">
              {showPartialRefundPolicyLabel ? 'Partial refund' : 'Flexible cancellation'}
            </BodyText>
            {' '}
            options available
          </BodyText>
        )}
        {!promotion && !hasMultipleCancellationPolicies && <BedbankCancellationPolicy
          rate={rate}
          offer={offer}
          showNonCancellation
        />}
        <LoyaltyPoints
          calculationRequests={pointsEarnCalculationRequests}
          calculationType={!checkInDate ? 'estimate' : 'exact'}
        />
      </Group>

      {luxPlusLPPEnabled && totalPrices.totals.memberPrice > 0 && <LabelWrapper>
        <LuxPlusLabelWithModal
          saveAmount={totalPrices.totals.price - totalPrices.totals.memberPrice}
          type="member-price-offer-lpp"
          hideSaveAmountPlus
        />
      </LabelWrapper>}

      <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
        <BedbankPackageTileCheapestPricing
          promotions={promotion}
          rate={rate}
          bundleRate={bundleRate}
          rooms={rooms}
          offer={offer}
          checkIn={checkInDate}
          packageView={packageView}
          detailsModalMode={detailsModalMode}
        />
      </WalledContent>
    </Group>
  )
}

export default withOfferPageAndReduxState((offerState, appState) => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  rooms: offerState.rooms,
  flightOrigin: offerState.airport,
  subscriber: getEnquirySubscriber(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isSpoofed: appState.auth.account.isSpoofed,
  originAirport: offerState.airport,
  tripPlannerTripId: appState.tripPlanner.currentTripId,
}), BedbankRoomCheapestOptionRateMobile)
